<template>
  <div class="flex flex-col gap-3">
    <button
      type="button"
      class="flex min-w-0 items-center gap-3 px-5 py-3 hover:bg-selected-filter w-full"
      @click="isOpen = !isOpen"
    >
      <SvgoIconComponentGIconArrowFilter :class="[
          isOpen ? 'rotate-0' : 'rotate-180',
          'text-extra-text-color',
          'transition-transform duration-200',
        ]"></SvgoIconComponentGIconArrowFilter>
      <span
        class="min-w-0 flex-1 text-start text-ellipsis-1 text-fluid-label"
        :class="'font-helvetica-400 font-normal text-gray-dark'"
      >
        <slot />
      </span>
      <span
        v-if="selectedLabel"
        class="hidden max-w-[40%] truncate rounded-full bg-selected-filter px-2 py-1 text-[12px] font-helvetica-700 text-orange lg:block"
      >
        {{ selectedLabel }}
      </span>
    </button>

    <div v-if="isOpen" class="mt-1 pb-4">
      <div v-if="useChipLayout" class="flex flex-wrap gap-2 px-5">
        <button
          v-for="value in visibleValues"
          :key="value.value"
          type="button"
          class="inline-flex items-center justify-center rounded-full border text-[14px] transition-colors"
          :class="
            value.selected
              ? `${getMobileChipSizeClass(value.label)} border-orange bg-selected-filter text-orange`
              : `${getMobileChipSizeClass(value.label)} border-gray-border bg-white text-gray-dark`
          "
          @click="selectValue(value.value)"
        >
          {{ value.label }}
        </button>
      </div>
      <div v-else class="max-h-[260px] overflow-y-auto pr-1 brand-scroll">
        <ul class="space-y-1">
          <li
            v-for="value in visibleValues"
            :key="value.value"
            style="list-style: none"
          >
            <div
              class="w-full hover:bg-selected-filter"
              :class="value.selected ? 'bg-selected-filter' : ''"
            >
              <button
                type="button"
                class="flex w-full cursor-pointer items-center gap-3 px-5 py-[6px] text-left"
                @click="selectValue(value.value)"
              >
                <span
                  class="inline-flex h-4 w-4 items-center justify-center rounded-full border"
                  :class="[
                    'border-orange',
                    value.selected ? 'bg-orange/10' : 'bg-white',
                  ]"
                >
                  <span
                    v-if="value.selected"
                    class="h-2.5 w-2.5 rounded-full bg-orange"
                  ></span>
                </span>
                <span class="font-helvetica text-[16px] text-gray-dark">
                  {{ value.label }}
                </span>
              </button>
            </div>
          </li>
        </ul>
      </div>
      <div class="mt-3.5 flex items-center justify-between px-5">
        <button
          v-if="showToggle"
          type="button"
          class="flex items-center gap-1.5 text-[12px] font-helvetica-400 font-normal text-orange"
          @click="showAll = !showAll"
        >
          <span class="border-b border-dashed border-orange">
            {{ showAll ? "Свернуть" : "Показать всё" }}
          </span>
          <SvgoIconComponentGIconArrowFilterSmall :class="showAll ? 'rotate-180' : 'rotate-0'" class="transition-transform duration-200e"></SvgoIconComponentGIconArrowFilterSmall>
        </button>

        <button
          type="button"
          class="ml-auto text-[14px] font-helvetica text-gray-light border-b border-dashed border-gray-light cursor-pointer"
          @click="reset"
        >
          Сбросить
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { FilterValues } from "~/types/filterValues";

const props = defineProps<{
  values: FilterValues[];
  open?: boolean;
  mobile?: boolean;
}>();

const emit = defineEmits<{
  (e: "update:values", value: FilterValues[]): void;
}>();

const isOpen = ref(props.open ?? true);
const showAll = ref(false);
const initialCount = 6;

const isZeroCount = (value: FilterValues) =>
  typeof value.count === "number" && value.count <= 0;

const sortFilterValue = (a: FilterValues, b: FilterValues) => {
  const selectedDiff = Number(b.selected) - Number(a.selected);
  if (selectedDiff !== 0) return selectedDiff;

  return Number(isZeroCount(a)) - Number(isZeroCount(b));
};

const sortedValues = computed(() => [...props.values].sort(sortFilterValue));

const showToggle = computed(() => sortedValues.value.length > initialCount);
const visibleValues = computed(() =>
  showAll.value
    ? sortedValues.value
    : sortedValues.value.slice(0, initialCount),
);
const selectedLabel = computed(
  () => props.values.find((value) => value.selected)?.label ?? "",
);
const useChipLayout = computed(() => {
  return Boolean(props.mobile);
});

const getMobileChipSizeClass = (label: string) => {
  const normalized = label.trim();
  const isCompactNumeric = /^[0-9]{1,2}$/.test(normalized);

  if (useChipLayout.value && isCompactNumeric) {
    return "h-12 min-w-12 px-0 font-helvetica-700";
  }

  return "min-h-11 px-3 py-2 font-helvetica-400";
};

watch(
  () => props.open,
  (val) => {
    if (typeof val === "boolean") {
      isOpen.value = val;
    }
  },
);

const selectValue = (value: string) => {
  const updated = props.values.map((v) => ({
    ...v,
    selected: v.value === value,
  }));
  emit("update:values", updated);
};

const reset = () => {
  const updated = props.values.map((v) => ({ ...v, selected: false }));
  emit("update:values", updated);
};
</script>

<style scoped>
.brand-scroll::-webkit-scrollbar {
  width: 8px;
}

.brand-scroll::-webkit-scrollbar-thumb {
  background-color: var(--color-gray-light);
}

.brand-scroll::-webkit-scrollbar-track {
  background-color: var(--color-gray-track);
}
</style>
